import React from 'react'
import { Table, Col, Row, Form } from "react-bootstrap"
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/mobile';
import Footer from './Footer';
import { Offer } from '../types/offerForm';
import Hub3QrCode from './BankPaymentBarcodeQrcode';

interface ShowOfferDetailsProps {
  offer: Offer;
}

const ShowOfferDetails: React.FC<ShowOfferDetailsProps> = ({ offer }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();

  return (
    <div id="invoiceCapture" >
      <div className="d-flex flex-row justify-content-between align-items-start bg-light w-100 p-4">
        <div className="w-100">
          <h4 className="fw-bold my-2">{offer?.from || 'Senad Mandžić'}</h4>
          <h6 className="fw-bold text-secondary mb-1">
            {t('offer')}: {offer?.offerNumber || ''}
          </h6>
        </div>         
        <div className="text-end ms-4">
          <h6 className="fw-bold mt-1 mb-2">{t('due')}&nbsp;{t('amount')}:</h6>
          <h5 className="fw-bold text-secondary"> {offer?.currency} {offer?.total}</h5>
        </div>
      </div>            

      <div className="p-4 bg-white">
        <Row className="mb-4">
          <Col className={isMobile? 'col-md-6' : 'col-12 col-md-6'}>
            <div className="fw-bold">{t('to')}:</div>
            <div>{offer?.to || ''}</div>
            <div>{offer?.toAddress || ''}</div>
            <div>{offer?.toEmail || ''}</div>
            <div>{offer?.toOib || ''}</div>
          </Col>
          <Col className={isMobile? 'col-md-6' : 'col-12 col-md-6'}>
            <div className="fw-bold">{t('from')}:</div>
            <div>{offer?.from || ''}</div>
            <div>{offer?.fromAddress || ''}</div>
            <div>{offer?.fromEmail || ''}</div>
            <div>{offer?.fromOib || ''}</div>
          </Col> 
        </Row>
        <Row className="mb-1">
          <Col className={isMobile? 'col-md-8' : 'col-12 col-md-8'}>
                  <div className="bg-white border-top border-primary border-3 shadow-sm rounded p-4 h-100 d-flex justify-content-center align-items-center" >

                      <Hub3QrCode
                        payerName={offer?.to}
                        payerAddress={offer?.toAddress}
                        payerCity=""              
                        recipientName={offer?.from} 
                        recipientAddress={offer?.fromAddress || ''} 
                        recipientCity=""
                        recipientIBAN={offer?.iban}
                        model="HR00"
                        reference={`${offer?.offerNumber.substring(4)}-${offer?.toOib}`}
                        amount={offer?.total}
                        currency="EUR"
                        purpose={`${t('offerNumber')} ${offer?.offerNumber}`}
                      />

                  </div>
            </Col>
            <Col className={isMobile? 'col-md-6' : 'col-12 col-md-4'}>
                <div className="fw-bold mt-2">{t('validUntil')}:</div>
                <div>{offer?.validUntil || ''}</div>
                <div className="fw-bold mt-2">{t('iban')}:</div>
                <div>{offer?.iban || ''}</div>
                <div className="fw-bold mt-2">{t('referenceNumber')}:</div>
                <div>{`${offer?.offerNumber}-${offer?.toOib}` || '' }</div>
                {offer?.madeBy && (
                  <>
                    <div className="fw-bold mt-2">{t('madeBy')}:</div>
                    <div>{offer?.madeBy}</div>
                  </>
                )}        
              </Col>
        </Row>
        <Table className="mb-0">
          <thead>
            <tr>
              <th>{t('item.quantity')}</th>
              <th>{t('item.description')}</th>
              <th className="text-end">{t('item.price')}</th>
              <th className="text-end">{t('item.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {offer?.items.map((item, i) => {
              return (
                <tr id={i.toString()} key={i}>
                  <td style={{ width: '70px' }}>
                    {item?.quantity}
                  </td>
                  <td>
                    {item?.name} - {item?.description}
                  </td>
                  <td className="text-end" style={{ width: '100px' }}>{offer?.currency} {item?.price}</td>
                  <td className="text-end" style={{ width: '100px' }}>{offer?.currency} {(parseFloat(item?.price) * item?.quantity).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Table>
          <tbody>
            <tr>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
            <tr className="text-end">
              <td></td>
              <td className="fw-bold" style={{ width: '100px' }}>{t('item.subtotal')}</td>
              <td className="text-end" style={{ width: '100px' }}>{offer?.currency} {offer?.subTotal}</td>
            </tr>
            {offer?.taxAmmount !== '0.00' &&
              <tr className="text-end">
                <td></td>
                <td className="fw-bold" style={{ width: '100px' }}>{t('item.tax')}</td>
                <td className="text-end" style={{ width: '100px' }}>{offer?.currency} {offer?.taxAmmount}</td>
              </tr>
            }
            {offer?.discountAmmount !== '0.00' &&
              <tr className="text-end">
                <td></td>
                <td className="fw-bold" style={{ width: '100px' }}>{t('item.discount')}</td>
                <td className="text-end" style={{ width: '100px' }}>{offer?.currency} {offer?.discountAmmount}</td>
              </tr>
            }
            <tr className="text-end">
              <td></td>
              <td className="fw-bold" style={{ width: '100px' }}>{t('item.total')}</td>
              <td className="text-end" style={{ width: '100px' }}>{offer?.currency} {offer?.total}</td>
            </tr>
          </tbody>
        </Table>       
        
        <Row className='p-0' style={{ minHeight: '100px' }}>                        
            <div className="bg-white shadow-sm rounded p-4 d-flex flex-column">
              {offer?.remark && (<Form.Label className="fw-bold d-block mb-2">{t('remark')}</Form.Label>)}
              <div className="d-flex flex-column">
                <div>{offer?.taxRate  === '0.00' || offer?.taxRate === '0' ? t('statement.tax') : ''}</div>
                <div className="text-muted mt-50 mt-3" style={{ whiteSpace: 'pre-wrap', marginTop: 'auto' }}>
                  {offer?.remark}
                </div>
              </div>
            </div>                          
        </Row> 
      </div>
        <Footer text={'statement.footer.offer'}></Footer>
    </div>
  )
}
export default ShowOfferDetails;
